/**
 * Player state — an Entity with stance, stamina, and facing direction.
 * Headless; no PixiJS. See CLAUDE.md architecture rule 4.
 */

import { CONFIG } from '../config';
import type { DisguiseKind } from './disguise';
import { createHumanoidBody, movementMultiplier, type BodyState } from './body';
import type { Item } from './inventory';
import { createHeal, createKnife, createSilencedPistol, createSmg, type Weapon } from './weapons';
import type { Entity, EntityId, World } from './world';

export type Stance = 'run' | 'walk' | 'crouch';

export interface Player extends Entity {
  stance: Stance;
  /** 0..CONFIG.stamina.max. Drains while running, regens otherwise. */
  stamina: number;
  /**
   * True after stamina depletion; blocks the run stance until stamina
   * recovers past `CONFIG.stamina.recoverThreshold`. Prevents 1-tick
   * run/walk oscillation at empty.
   */
  exhausted: boolean;
  /**
   * Direction the player is facing (in {-1,0,1}). Updated on any movement;
   * retained while idle. Used by vision cones + focus lock.
   */
  facingDx: number;
  facingDy: number;
  /** RimWorld-lite body sim (see body.ts). */
  body: BodyState;
  /** Four weapon slots — ♣ Main (SMG), ♦ Side (pistol), ♠ Melee (knife), ♥ Heal (medkit). */
  weapons: [Weapon, Weapon, Weapon, Weapon];
  /** Active weapon slot: 0/1/2/3 (mapped from keys 1/2/3/4). */
  activeWeaponIdx: 0 | 1 | 2 | 3;
  /** "Dungeon loot" — items picked up from containers/corpses (the mop, keys, …).
   *  Shown in the inventory screen; NOT in the weapon taskbar. */
  dungeonLoot: Item[];
  /** Always-carried lockpicking tools (hidden — not in the taskbar). They gate
   *  the deferred pick/rake minigame. */
  tools: { rake: boolean; pick: boolean };
  /** World-space unit vector pointing from the player toward the last attacker —
   *  drives the directional "F Focus!" prompt. */
  hitDirX: number;
  hitDirY: number;
  /** Seconds the "F Focus!" prompt stays up after a hit (0 = hidden). */
  focusPromptTimer: number;
  /** True when a night-vision device is equipped (N does nothing without it). */
  hasNightVision: boolean;
  /** Night vision toggled ON (N). Brightens the world by `nvBoost`, tints it
   *  green + monochrome, and blows out already-bright tiles to white. */
  nightVisionOn: boolean;
  /** Latch: the goggles have already flash-blinded us for THIS spell in bright
   *  light. Set when overwhelmed, cleared once back in tolerable light — so the
   *  goggles stay ON (green keeps rendering) without re-blinding every cycle while
   *  you stand in the glare. */
  nvOverwhelmed: boolean;
  /** Focus MODE toggle. When true the player is in focus mode: it auto-locks
   *  the nearest perceived target, or stays "primed" (searching, no lock) when
   *  none is perceivable — priming holds the torso aim so you can scan while
   *  moving. F toggles it. */
  focusActive: boolean;
  /** EntityId of the guard currently focus-LOCKED. null = primed (searching)
   *  while `focusActive`, or unfocused. */
  focusedTargetId: EntityId | null;
  /** Precomputed player FOV each tick — for focus target search. */
  fovTiles: Set<number> | null;
  /** Accumulated seconds fed to the reticule drift Lissajous curve. Ticks
   *  up while focused; frozen (but not reset) while unfocused so the
   *  reticule doesn't snap back on re-focus. */
  reticuleDriftT: number;
  /**
   * Guard entity id → world.tick when the player stops hearing them.
   * Populated per tick from fresh sound floods that reach the player's
   * tile; the renderer treats audible entities as visible so a moving
   * guard behind cover still appears as long as they make noise.
   */
  hearingUntil: Map<number, number>;
  /**
   * Manually-selected limb (index into BODY_LAYOUT) when the player has
   * cycled with arrow keys during focus. -1 = drift-based aim (reticule
   * wanders). Reset on focus toggle.
   */
  selectedLimbIdx: number;
  /**
   * The WASD held-key signature (e.g. "WD") that produced the current
   * `legDx/legDy` latch. `legDx/legDy` live on the base `Entity`; for the
   * player they are CAMERA-RELATIVE (W = up-on-screen/forward, A/D strafe),
   * re-based through the camera heading whenever the held combination changes,
   * and retained while idle. "" = no move key held. See movement.ts.
   */
  legKeySig: string;
  /**
   * Cover-shooter state. When `inCover` the player is locked to a wall border:
   * auto-crouched + hidden, slides along the wall, and peeks (stands, exposed)
   * only while aiming/firing. `coverDx/coverDy` is the cardinal NORMAL pointing
   * from the player toward the wall they're hugging. See movement.ts.
   */
  inCover: boolean;
  coverDx: number;
  coverDy: number;
  /**
   * Exposed beyond cover this tick — either standing up over the top (aim /
   * fire / hold C) or leaning around the side. Drives the peek→focus arm and
   * the rendered torso lean. False while tucked.
   */
  peekingOut?: boolean;
  /**
   * While side-peeking crouched, the world-space direction the torso pokes out
   * past the cover edge (0/0 when not side-peeking). The renderer offsets the
   * torso this way instead of standing it up.
   */
  coverLeanDx?: number;
  coverLeanDy?: number;
  /** Target tile of an in-progress "crawl over a half wall" (W in cover). `-1`
   *  = not crawling. Stored so the target doesn't drift as the player moves. */
  coverCrawlX?: number;
  coverCrawlY?: number;
  /** Target tile of an in-progress SPRINT VAULT over a half wall (started by
   *  running into one). `-1` = not vaulting. Committed like the cover crawl. */
  vaultX?: number;
  vaultY?: number;
  /**
   * Current-frame WASD move intent (in {-1,0,1}). 0/0 when no move key is
   * held (unlike `legDx/legDy`, which retains the last direction). Consumed
   * by the movement dev panel.
   */
  moveIntentDx: number;
  moveIntentDy: number;
  /**
   * Seconds the player has been standing still (no move key held). Once it
   * passes `CONFIG.move.snapDelaySec`, the free float `(fx, fy)` eases to the
   * nearest tile centre so the player comes to rest ON the grid a brief
   * moment after stopping. Reset to 0 whenever a move key is held.
   */
  restTimer: number;
  /**
   * Free floating-point sub-tile position (tile units) — the SOURCE OF TRUTH
   * for the player. Movement integrates velocity into (fx, fy) continuously;
   * the logical integer tile (x, y) is derived each tick as round(fx),
   * round(fy) so collision/FOV/sound/AI keep operating on the grid (arch
   * rule 2). On stop, after a brief delay (`CONFIG.move.snapDelaySec`),
   * (fx, fy) eases to the nearest tile centre and settles on the integer.
   * `visualX/Y` mirror (fx, fy) so the renderer draws the glyph anywhere
   * between tiles while moving.
   */
  fx: number;
  fy: number;
  /**
   * Prop concealment (see `sim/dungeon/propAction.ts`, the 'hide' verb): the
   * tile of the cabinet/locker the player has slipped into, or null when out in
   * the open. A concealed player is unseeable — except by an ALERT guard right
   * on top of the prop, which throws the door open (awareness.ts). Broken by
   * moving, by E, or by death.
   */
  concealedAt: { x: number; y: number } | null;
  /** Debug: ignore all incoming damage (`godmode` in the ` console). */
  godmode: boolean;
  /** Debug: no entity can perceive you (`ghost`). You're still solid and still
   *  make noise — it's invisibility, not incorporeality. */
  invisible: boolean;
  /** Weapon put away — an empty hand. Blocks attacking, and reads as harmless
   *  to anyone judging you (sim/disguise.ts), which makes sheathing part of a
   *  disguise rather than just a prerequisite for pickpocketing. */
  sheathed: boolean;
  /** The uniform currently worn (sim/disguise.ts), or null in your own clothes.
   *  Changes the question observers ask from "can I see them" to "do they belong
   *  here" — see `exposure`. */
  disguise: DisguiseKind | null;
  /** Loose objects carried for throwing (prop verbs 'take'/'throw'), capped at
   *  `CONFIG.prop.maxThrowables`. B hurls one along the player's facing. */
  throwables: number;
  /** Tile where the player died, or null while alive. The player entity itself
   *  becomes a free-fly ghost after death (its x/y roam the map), so the CORPSE
   *  stays pinned here — the renderer draws a body at this tile and blood trails
   *  resolve against it. Set on death, cleared on respawn. */
  deathTile: { x: number; y: number } | null;
}

export function spawnPlayer(world: World, x: number, y: number): Player {
  const base = world.spawn({
    glyph: '@',
    // Neon cyan — style.ts COLOR_PLAYER. Not imported here to keep the
    // sim layer free of render-module deps (arch rule 4).
    fg: 0x22e6ff,
    bg: -1,
    x,
    y,
  });
  const p: Player = {
    ...base,
    stance: 'walk',
    stamina: CONFIG.stamina.max,
    exhausted: false,
    facingDx: 0,
    facingDy: -1,
    body: createHumanoidBody(),
    weapons: [createSmg(), createSilencedPistol(), createKnife(), createHeal()],
    activeWeaponIdx: 2, // knife by default — reasonable stealth loadout
    dungeonLoot: [],
    tools: { rake: true, pick: true }, // always carried; hidden from the taskbar
    hitDirX: 0,
    hitDirY: 0,
    focusPromptTimer: 0,
    hasNightVision: true, // NV device equipped so N works out of the box
    nightVisionOn: false,
    nvOverwhelmed: false,
    focusActive: false,
    focusedTargetId: null,
    fovTiles: null,
    reticuleDriftT: 0,
    hearingUntil: new Map(),
    selectedLimbIdx: -1,
    legDx: 0,
    legDy: -1,
    legKeySig: '',
    inCover: false,
    concealedAt: null,
    godmode: false,
    invisible: false,
    sheathed: false,
    disguise: null,
    throwables: 0,
    coverDx: 0,
    coverDy: 0,
    peekingOut: false,
    coverLeanDx: 0,
    coverLeanDy: 0,
    coverCrawlX: -1,
    coverCrawlY: -1,
    vaultX: -1,
    vaultY: -1,
    moveIntentDx: 0,
    moveIntentDy: 0,
    restTimer: 0,
    fx: x,
    fy: y,
    deathTile: null,
  };
  world.entities.set(p.id, p);
  return p;
}

/** Speed in tiles/sec for the player's current stance, halved by leg crippling. */
export function speedFor(p: Player): number {
  const base = (() => {
    switch (p.stance) {
      case 'run':
        return CONFIG.speed.run;
      case 'walk':
        return CONFIG.speed.walk;
      case 'crouch':
        return CONFIG.speed.crouch;
    }
  })();
  return base * movementMultiplier(p.body);
}

/**
 * Teleport the player back to a fixed tile and reset volatile state. Debug
 * convenience so a stuck / off-map / dead player can recover with one keystroke.
 */
export function respawnPlayer(p: Player, x: number, y: number): void {
  p.x = x;
  p.y = y;
  p.fx = x;
  p.fy = y;
  p.moveDx = 0;
  p.moveDy = 0;
  p.moveProgress = 0;
  p.visualX = x;
  p.visualY = y;
  p.prevVisualX = x;
  p.prevVisualY = y;
  p.stamina = CONFIG.stamina.max;
  p.exhausted = false;
  p.stance = 'walk';
  // Full heal + full ammo — R is a debug reset.
  for (const part of ['head', 'torso', 'leftArm', 'rightArm', 'leftLeg', 'rightLeg'] as const) {
    p.body.hp[part] = p.body.hpMax[part];
    p.body.crippled[part] = false;
  }
  p.body.bleedPerSec = 0;
  p.body.dead = false;
  for (const w of p.weapons) {
    w.cooldown = 0;
    if (w.magazine !== undefined) w.ammo = w.magazine;
  }
  p.focusActive = false;
  p.focusedTargetId = null;
  p.focusPromptTimer = 0;
  p.hearingUntil.clear();
  p.legDx = 0;
  p.legDy = -1;
  p.legKeySig = '';
  p.inCover = false;
  p.coverDx = 0;
  p.coverDy = 0;
  p.peekingOut = false;
  p.coverLeanDx = 0;
  p.coverLeanDy = 0;
  p.coverCrawlX = -1;
  p.coverCrawlY = -1;
  p.vaultX = -1;
  p.vaultY = -1;
  p.moveIntentDx = 0;
  p.moveIntentDy = 0;
  p.restTimer = 0;
  p.facingDx = 0;
  p.facingDy = -1;
  p.deathTile = null;
  p.dungeonLoot = [];
}
